(() => {
  const OVERLAY_SELECTOR = '[data-bn-bundle-lightbox]';
  const IMAGE_SELECTOR = '.bn-bundle-lightbox__image';
  const TRIGGER_SELECTOR = '[data-bn-bundle-lightbox-trigger]';
  const CLOSE_SELECTOR = '.bn-bundle-lightbox__close';
  const MAX_SCALE = 4;

  let pointers = new Map();
  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let dragState = null;
  let pinchState = null;
  let swipeState = null;
  let gestureMoved = false;
  let suppressClickUntil = 0;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const isTouchLike = (event) => event.pointerType === 'touch' || event.pointerType === 'pen';
  const getOverlay = (target = document) => target.closest?.(OVERLAY_SELECTOR) || document.querySelector(OVERLAY_SELECTOR);
  const getImage = (overlay) => overlay?.querySelector(IMAGE_SELECTOR);

  const resetState = () => {
    pointers.clear();
    scale = 1;
    translateX = 0;
    translateY = 0;
    dragState = null;
    pinchState = null;
    swipeState = null;
    gestureMoved = false;

    const overlay = getOverlay();
    const image = getImage(overlay);
    if (image) image.style.transform = '';
    overlay?.classList.remove('is-zoomed');
  };

  const clampTranslation = (overlay, image) => {
    if (scale <= 1) {
      translateX = 0;
      translateY = 0;
      return;
    }

    const scaledWidth = image.clientWidth * scale;
    const scaledHeight = image.clientHeight * scale;
    const maxX = Math.max(0, (scaledWidth - overlay.clientWidth) / 2);
    const maxY = Math.max(0, (scaledHeight - overlay.clientHeight) / 2);
    translateX = clamp(translateX, -maxX, maxX);
    translateY = clamp(translateY, -maxY, maxY);
  };

  const applyTransform = (overlay, image) => {
    clampTranslation(overlay, image);
    image.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
    overlay.classList.toggle('is-zoomed', scale > 1.01);
  };

  const setScale = (nextScale, overlay, image) => {
    scale = clamp(nextScale, 1, MAX_SCALE);
    if (scale <= 1.01) {
      scale = 1;
      translateX = 0;
      translateY = 0;
    }
    applyTransform(overlay, image);
  };

  const distance = (first, second) => Math.hypot(second.x - first.x, second.y - first.y);

  const startPinch = () => {
    const active = Array.from(pointers.values());
    if (active.length !== 2) {
      pinchState = null;
      return;
    }

    pinchState = {
      distance: distance(active[0], active[1]),
      scale,
    };
    dragState = null;
    swipeState = null;
    gestureMoved = true;
  };

  document.addEventListener('dblclick', (event) => {
    if (!event.target.closest?.(OVERLAY_SELECTOR)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('pointerdown', (event) => {
    const overlay = getOverlay(event.target);
    if (!overlay || overlay.hidden || !isTouchLike(event) || event.target.closest?.(CLOSE_SELECTOR)) return;

    const image = getImage(overlay);
    if (!image) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    overlay.setPointerCapture?.(event.pointerId);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.size === 1) gestureMoved = false;

    if (pointers.size === 2) {
      startPinch();
      return;
    }

    if (scale > 1.01) {
      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        translateX,
        translateY,
      };
    } else {
      swipeState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
      };
    }
  }, true);

  document.addEventListener('pointermove', (event) => {
    if (!pointers.has(event.pointerId)) return;

    const overlay = getOverlay(event.target);
    const image = getImage(overlay);
    if (!overlay || !image) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.size === 2) {
      const active = Array.from(pointers.values());
      if (!pinchState) startPinch();
      if (!pinchState || pinchState.distance <= 0) return;
      setScale(pinchState.scale * (distance(active[0], active[1]) / pinchState.distance), overlay, image);
      gestureMoved = true;
      return;
    }

    if (dragState?.pointerId === event.pointerId && scale > 1.01) {
      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) gestureMoved = true;
      translateX = dragState.translateX + deltaX;
      translateY = dragState.translateY + deltaY;
      applyTransform(overlay, image);
    }
  }, true);

  const finishPointer = (event) => {
    if (!pointers.has(event.pointerId)) return;

    const overlay = getOverlay(event.target);
    if (!overlay) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const currentSwipe = swipeState?.pointerId === event.pointerId ? swipeState : null;
    pointers.delete(event.pointerId);
    overlay.releasePointerCapture?.(event.pointerId);

    if (pointers.size === 1 && scale > 1.01) {
      const [pointerId, point] = pointers.entries().next().value;
      dragState = {
        pointerId,
        startX: point.x,
        startY: point.y,
        translateX,
        translateY,
      };
    } else if (pointers.size === 2) {
      startPinch();
    } else if (pointers.size === 0) {
      dragState = null;
      pinchState = null;
    }

    if (currentSwipe && scale === 1) {
      const deltaX = event.clientX - currentSwipe.startX;
      const deltaY = event.clientY - currentSwipe.startY;
      if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) gestureMoved = true;
      if (deltaY > 90 && Math.abs(deltaX) < 70) {
        suppressClickUntil = Date.now() + 350;
        overlay.querySelector(CLOSE_SELECTOR)?.click();
      }
    }

    if (swipeState?.pointerId === event.pointerId) swipeState = null;
    if (gestureMoved) suppressClickUntil = Date.now() + 350;
  };

  document.addEventListener('pointerup', finishPointer, true);
  document.addEventListener('pointercancel', finishPointer, true);

  document.addEventListener('click', (event) => {
    if (event.target.closest?.(TRIGGER_SELECTOR)) {
      window.requestAnimationFrame(resetState);
      return;
    }

    if (Date.now() >= suppressClickUntil || !event.target.closest?.(OVERLAY_SELECTOR)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('load', (event) => {
    if (event.target.matches?.(IMAGE_SELECTOR)) resetState();
  }, true);

  window.addEventListener('resize', () => {
    const overlay = getOverlay();
    const image = getImage(overlay);
    if (overlay && image && !overlay.hidden) applyTransform(overlay, image);
  });
})();
